import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { ApiError } from '@/api/http';
import { useSession } from '@/app/session';
import { PasswordInput } from './PasswordInput';
import './session.css';

/**
 * Re-authentication, over the app, without unmounting it.
 *
 * The whole point is what does **not** happen: no navigation, no remount, no
 * cache clear. A 401 arriving while someone is three paragraphs into a reply
 * must not cost them the reply, so the app stays exactly where it was and this
 * modal is rendered on top of it. A redirect to `/login` would be a data loss
 * bug wearing the costume of a security measure.
 *
 * The address is pre-filled and read-only: this is a re-auth of a known
 * session, not a sign-in chooser. Changing account here would mean discarding
 * the state we just went to such trouble to preserve — the "Sign out" button is
 * the honest way to do that, and it says what it will do.
 *
 * ## Two modes, one dialog
 *
 * - **`expired`** — the session is gone and a password will start a new one.
 * - **`recovery`** — the session is *fine*; this browser's device key is not
 *   (ADR 0042). Cookies alive, key evicted, is the **common** path on a 30-day
 *   remembered session, and it must never be a redirect to `/login` or a
 *   logout loop: the shell stays rendered behind this dialog, the cache is
 *   kept, other tabs are unaffected, and a correct password calls
 *   `POST /auth/rebind` to enrol a fresh key in place. The copy says why,
 *   because "confirm your password" with no reason reads like a phishing
 *   prompt in the user's own mail client.
 */
export function ReauthDialog({ mode = 'expired' }: { mode?: 'expired' | 'recovery' } = {}) {
  const {
    account,
    binding,
    pending,
    reauthenticate,
    rebindDevice,
    logout,
    online,
    expiryReason,
    flushPending,
    discardPending,
  } = useSession();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replayPrompt, setReplayPrompt] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const passwordId = useId();

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  const queued = pending.length;
  const manual = pending.filter((action) => action.kind !== 'send');

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'recovery') {
        // Not a re-auth: the session never ended. This enrols a new key on the
        // session that is still running underneath this dialog.
        await rebindDevice(password);
        setPassword('');
        setBusy(false);
        return;
      }
      await reauthenticate(password);
      setPassword('');
      // Sends have already flushed; anything else asks before it replays.
      if (manual.length > 0) setReplayPrompt(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : cause instanceof Error
            ? cause.message
            : 'Sign-in failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (replayPrompt) {
    return (
      <div className="reauth__scrim">
        <section
          className="reauth__dialog card"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <h2 className="reauth__title" id={titleId}>
            Replay what was waiting?
          </h2>
          <p className="reauth__body">
            These actions were queued while you were signed out. Nothing has been sent to the server
            yet.
          </p>
          <ul className="reauth__queue">
            {manual.map((action) => (
              <li key={action.id}>{action.label}</li>
            ))}
          </ul>
          <div className="reauth__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                discardPending();
                setReplayPrompt(false);
              }}
            >
              Discard
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                void flushPending('all').then(() => setReplayPrompt(false));
              }}
            >
              Replay {manual.length} action{manual.length === 1 ? '' : 's'}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="reauth__scrim">
      <form
        className="reauth__dialog card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onSubmit={(event) => void onSubmit(event)}
      >
        <h2 className="reauth__title" id={titleId}>
          {mode === 'recovery' ? 'Confirm your password for this device' : 'Your session ended'}
        </h2>
        <p className="reauth__body" id={descriptionId}>
          {mode === 'recovery'
            ? recoveryExplanation(binding.supported, binding.recoveryReason)
            : expiryReason === 'offline'
              ? 'You are offline and your session has expired. You can keep reading cached mail; sending and syncing need you to sign in again.'
              : 'Sign in again to carry on. Everything you were working on is still here.'}
        </p>
        {mode === 'recovery' && (
          <p className="reauth__body muted">
            You are still signed in and nothing you were working on has been lost.
          </p>
        )}

        {error !== null && (
          <p className="reauth__error" role="alert">
            {error}
          </p>
        )}

        {queued > 0 && (
          <p className="reauth__queue" role="status">
            {queued} action{queued === 1 ? '' : 's'} will be retried after you sign in.
          </p>
        )}

        {/*
          * Shown only when we know whose session this is. At boot on a lost
          * key the whoami never answered, so there is no address to pre-fill —
          * and an empty read-only field would be a worse prompt than none. The
          * server resolves the owner from the cookie either way.
          */}
        {account !== undefined && (
          <label className="reauth__field">
            <span>Email address</span>
            <input
              className="input"
              type="email"
              autoComplete="username"
              value={account.email}
              readOnly
            />
          </label>
        )}

        {/* The address is shown just above when known, so "Password" is
            enough here (ADR 0094). Labelled by `for`: a wrapping label would
            add the Show password button's name to the field's own. */}
        <div className="reauth__field">
          <label htmlFor={passwordId}>Password</label>
          <PasswordInput
            ref={passwordRef}
            id={passwordId}
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {!online && (
          <p className="reauth__body" role="status">
            You are offline, so signing in will not work until the connection is back. Your queued
            work is kept until then.
          </p>
        )}

        <div className="reauth__actions">
          <button type="button" className="btn btn--ghost" onClick={() => void logout()}>
            Sign out
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || !online}>
            {busy
              ? mode === 'recovery'
                ? 'Confirming…'
                : 'Signing in…'
              : mode === 'recovery'
                ? 'Continue'
                : 'Sign in'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * One honest sentence about why a password is being asked for.
 *
 * Specific, because a vague prompt in a mail client is indistinguishable from
 * a phishing overlay — and because the three causes have genuinely different
 * explanations. `unsupported` is the one case where the password cannot help,
 * and saying so beats a dialog the user can only fail.
 */
function recoveryExplanation(
  supported: boolean,
  reason: 'required' | 'invalid' | 'stale' | 'replayed' | null,
): string {
  if (!supported) {
    return 'This browser cannot store a session key, so this device cannot be protected against cookie theft. Signing in again will not change that — a different browser, or one that allows site data, will.';
  }
  switch (reason) {
    // Not "another tab re-confirmed it": that case re-reads the key and
    // retries on its own, so it never reaches this dialog, and naming other
    // tabs or windows sends the user looking for a cause that is not there.
    case 'invalid':
      return 'This browser\u2019s session key no longer matches the one on record. Enter your password to re-confirm it on this device.';
    case 'stale':
    case 'replayed':
      return 'This browser could not prove it holds this session\u2019s key. Enter your password to re-confirm it on this device.';
    default:
      return 'This browser\u2019s session key is missing \u2014 that happens after clearing site data, or when a browser reclaims storage. Enter your password to put a new one in place.';
  }
}
